/**
 * OWNER-OPERATING-SYSTEM-V1 -- Leakage board (/dashboard/leakage).
 *
 * ARCHETYPE: QUEUE/BOARD (F4), re-rendered to the 05/06 polish standard
 * per finding F-V3-3: COUNT-AS-HERO class cells (severity tint only on a
 * crossed threshold -- the tile-tint rule), severity-ordered evidence rows,
 * plain-word escalation, every count drilling to its rows.
 *
 * COUNT-ONLY BY FOUNDER SILENCE: rupee-value-per-row is UNANSWERED, so NO
 * money renders anywhere on this board (the run record names the deferral).
 *
 * Reads: THE ONE classifier wire (GET /api/sla-leakage -- roles owner /
 * gm_agm / regional_head / call_center_agent; the server fence is the
 * authority) + the flag-gated booking-cohort funnel (me signal
 * ownerLeakageFunnelEnabled; the re-homed Overview capability, COUNT
 * fields only). Row identities (lead names) are LAWFUL here -- the same
 * wire already serves them to this audience; row click opens the lead
 * record (EVIDENCE-ROWS law).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchSlaLeakage, fetchOwnerLeakageSnapshot } from '@/dashboard/api';
import type { OwnerLeakageSnapshot } from '@/dashboard/types';
import type {
  SlaBreachItemView,
  SlaLeakageResult,
} from '@/dashboard/sla-leakage/sla-leakage-view';
import {
  assigneeLabel,
  classShortLabel,
  displayNameOf,
  formatLeakageAge,
  itemActionHint,
} from '@/dashboard/sla-leakage/sla-leakage-view';
import { useDashboardMeContext } from '@/dashboard/me-context';
import {
  DataTable,
  EmptyState,
  KpiBand,
  LoadingState,
  PageHeader,
  SectionBand,
  type DataTableColumn,
  type KpiBandCellDef,
} from '@/dashboard/ui';

function severityRank(item: SlaBreachItemView): number {
  return item.severity === 'red' ? 0 : item.severity === 'amber' ? 1 : 2;
}

function itemColumns(): DataTableColumn<SlaBreachItemView>[] {
  return [
    {
      key: 'person',
      header: 'Person',
      render: (r) => (
        <span>
          <strong>{displayNameOf(r)}</strong>{' '}
          <span className="ac-kcell__meta">{r.stageLabel ?? ''}</span>
        </span>
      ),
    },
    {
      key: 'issue',
      header: 'Issue',
      render: (r) => classShortLabel(r.breachClass),
    },
    { key: 'clinic', header: 'Clinic', render: (r) => r.clinicLabel ?? '--' },
    { key: 'owner', header: 'Assigned', render: (r) => assigneeLabel(r) },
    {
      key: 'age',
      header: 'Waiting',
      align: 'end',
      render: (r) => (
        <span className={`ac-age ac-age--${r.severity === 'red' ? 'red' : 'amber'}`}>
          {formatLeakageAge(r.ageMinutes)}
        </span>
      ),
    },
    { key: 'next', header: 'Next', render: (r) => itemActionHint(r) },
  ];
}

export default function OwnerLeakageBoardPage(): JSX.Element {
  const navigate = useNavigate();
  const meCtx = useDashboardMeContext();
  const cohortEnabled = meCtx?.me?.ownerLeakageFunnelEnabled === true;

  const [board, setBoard] = useState<SlaLeakageResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [cohort, setCohort] = useState<OwnerLeakageSnapshot | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSlaLeakage()
      .then((data) => {
        if (!cancelled) {
          setBoard(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Load failed');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!cohortEnabled) return;
    let cancelled = false;
    fetchOwnerLeakageSnapshot({ days: 7, mode: 'real' })
      .then((data) => {
        if (!cancelled) setCohort(data);
      })
      .catch(() => {
        // Fault-isolated: the cohort section simply does not render.
      });
    return () => {
      cancelled = true;
    };
  }, [cohortEnabled]);

  const heroCells: KpiBandCellDef[] =
    board === null
      ? []
      : board.summary.map((s) => ({
          key: s.breachClass,
          label: classShortLabel(s.breachClass),
          value: String(s.count),
          meta:
            s.count === 0
              ? 'clear'
              : s.severity === 'red'
                ? 'breaching -- worst past 2x threshold'
                : 'aging past threshold',
        }));

  const rows =
    board === null ? [] : [...board.items].sort((a, b) => severityRank(a) - severityRank(b));

  return (
    <div>
      <PageHeader
        title="Leakage"
        subs={[
          'Open work that is aging past its thresholds -- counts and owners; every row opens the person.',
        ]}
      />
      <div className="ac-band-flow">
        {loading ? <LoadingState label="Loading the leakage board" lines={3} /> : null}
        {error !== null && !loading ? (
          <EmptyState
            title="The leakage board did not load."
            body={`${error} -- reload the page to retry.`}
          />
        ) : null}

        {board !== null ? (
          <>
            <KpiBand
              title="Open by class"
              basis="counts only -- severity follows config thresholds"
              cells={heroCells}
              testId="leakage-class-band"
            />

            <SectionBand
              title="The rows"
              basis={board.capped ? 'large org -- the scan capped; counts are a lower bound' : undefined}
              testId="leakage-rows"
            >
              <DataTable
                columns={itemColumns()}
                rows={rows}
                rowKey={(r) => `${r.breachClass}:${r.refKind}:${r.safeLeadId ?? r.appointmentId ?? ''}`}
                stickyHeader
                countLine={`${rows.length} open items -- severity first, then age; row opens the person`}
                onRowClick={(r) => {
                  if (r.safeLeadId !== null) navigate(`/dashboard/leads/${r.safeLeadId}`);
                }}
                empty={
                  <EmptyState
                    title="Nothing is leaking right now."
                    body="Every lead and appointment is inside its thresholds. New breaches appear here as they age."
                  />
                }
                testId="leakage-items-table"
              />
            </SectionBand>

            {cohortEnabled && cohort !== null ? (
              <SectionBand
                title="Booking cohort (7 days)"
                basis="counts only -- the cohort funnel's stage counts"
                testId="leakage-cohort"
              >
                <KpiBand
                  title={`Booked ${cohort.windowStart} to ${cohort.windowEnd}`}
                  cells={[
                    { key: 'leads', label: 'Leads', value: String(cohort.totals.leads) },
                    { key: 'calls', label: 'Calls', value: String(cohort.totals.calls) },
                    { key: 'answered', label: 'Answered', value: String(cohort.totals.answered) },
                    { key: 'missed', label: 'Missed', value: String(cohort.totals.missed) },
                    { key: 'walkins', label: 'Walk-ins', value: String(cohort.totals.walkins) },
                  ]}
                />
              </SectionBand>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
